import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Calendar, MessageCircle, BookOpen, TrendingUp, Clock, CreditCard, Loader2, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './MentorshipPage.css';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_DETAILS = {
  'bi-weekly': {
    id: 'bi-weekly',
    name: '6 Month Mini Mentorship (Bi Weekly)',
    subtitle: 'Personalized Coaching Every Two Weeks',
    price: 1500,
    installmentPrice: 750,
    installmentCount: 2,
    sessionFrequency: 'Bi-Weekly',
    sessionsTotal: 12,
    liveTradingMonths: 6,
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    features: [
      { icon: Calendar, text: '12 bi-weekly 1-on-1 sessions' },
      { icon: TrendingUp, text: '6 months FREE Live Trading membership' },
      { icon: BookOpen, text: 'HyperDrive Instruction Manual included' },
      { icon: MessageCircle, text: 'Direct messaging with Coach CJ' },
      { icon: Check, text: 'Priority trade reviews' }
    ]
  },
  'weekly': {
    id: 'weekly',
    name: '6 Month Mentorship (Weekly)',
    subtitle: 'Intensive Weekly Coaching Sessions',
    price: 5000,
    installmentPrice: 2500,
    installmentCount: 2,
    sessionFrequency: 'Weekly',
    sessionsTotal: 24,
    liveTradingMonths: 12,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    popular: true,
    features: [
      { icon: Calendar, text: '24 weekly 1-on-1 sessions' },
      { icon: TrendingUp, text: '1 year FREE Live Trading membership' },
      { icon: BookOpen, text: 'HyperDrive Instruction Manual included' },
      { icon: MessageCircle, text: 'Direct messaging with Coach CJ' },
      { icon: Check, text: 'Priority trade reviews' },
      { icon: Check, text: 'Custom trading plan development' }
    ]
  }
};

const MentorshipPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  // Check if payment plan is requested via URL parameter
  const paymentType = searchParams.get('payment') === '2' ? 'installment' : 'full';
  const isInstallment = paymentType === 'installment';
  
  const plan = PLAN_DETAILS[planId];
  
  if (!plan) {
    return (
      <div className="mentorship-page">
        <div className="mentorship-error">
          <h1>Plan Not Found</h1>
          <p>This mentorship plan doesn't exist.</p>
        </div>
      </div>
    );
  }
  
  const handleCheckout = async () => {
    // If not logged in, redirect to login with return URL
    if (!isAuthenticated) {
      const returnUrl = window.location.pathname + window.location.search;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('hyperdriveToken');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }
      
      const response = await fetch(`${apiUrl}/api/mentorship/checkout/${planId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ payment_type: paymentType })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert(data.detail || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(`Failed to start checkout: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Display price based on payment type
  const displayPrice = isInstallment ? plan.installmentPrice : plan.price;
  const priceNote = isInstallment 
    ? `$${plan.installmentPrice.toLocaleString()} × ${plan.installmentCount} payments (2nd payment at month 3)`
    : 'One-time payment';
  
  // Button text based on auth status
  const getButtonContent = () => {
    if (loading) {
      return (
        <>
          <Loader2 className="spinner" size={20} />
          Processing...
        </>
      );
    }
    
    if (!isAuthenticated) {
      return (
        <>
          <LogIn size={20} />
          Sign Up to Enroll
        </>
      );
    }
    
    if (isInstallment) {
      return (
        <>
          <CreditCard size={20} />
          Start Payment Plan - ${plan.installmentPrice.toLocaleString()}
        </>
      );
    }
    
    return (
      <>
        <CreditCard size={20} />
        Enroll Now - ${plan.price.toLocaleString()}
      </>
    );
  };
  
  return (
    <div className="mentorship-page">
      <div className="mentorship-container">
        {/* Header */}
        <div className="mentorship-header">
          <div className="header-badge" style={{ background: plan.gradient }}>
            {plan.popular && <span className="popular-tag">Most Popular</span>}
            <span className="frequency-tag">{plan.sessionFrequency} Sessions</span>
          </div>
          <h1>{plan.name}</h1>
          <p className="subtitle">{plan.subtitle}</p>
          {isInstallment && (
            <div className="payment-plan-badge">2-Payment Plan</div>
          )}
        </div>
        
        {/* Pricing Card */}
        <div className="pricing-card" style={{ borderColor: plan.color }}>
          <div className="price-section">
            <div className="price-display">
              <div className="price-amount" style={{ color: plan.color }}>${displayPrice.toLocaleString()}</div>
              <div className="price-note">{priceNote}</div>
            </div>
          </div>
          
          {/* Features */}
          <div className="features-section">
            <h3>What's Included</h3>
            <ul className="features-list">
              {plan.features.map((feature, index) => (
                <li key={index}>
                  <feature.icon size={20} style={{ color: plan.color }} />
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* CTA Button */}
          <button 
            className="checkout-btn"
            onClick={handleCheckout}
            disabled={loading || authLoading}
            style={{ background: plan.gradient }}
            data-testid="mentorship-checkout-btn"
          >
            {getButtonContent()}
          </button>
          
          {!isAuthenticated && (
            <p className="auth-note">
              Already have an account? <a href={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}>Log in</a>
            </p>
          )}
          
          <p className="secure-note">
            <Clock size={14} />
            Secure checkout powered by Stripe
          </p>
        </div>
        
        {/* Additional Info */}
        <div className="info-section">
          <h3>How It Works</h3>
          <div className="info-steps">
            <div className="step">
              <div className="step-number" style={{ background: plan.gradient }}>1</div>
              <div className="step-content">
                <h4>Complete Purchase</h4>
                <p>Secure payment through Stripe</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number" style={{ background: plan.gradient }}>2</div>
              <div className="step-content">
                <h4>Instant Access</h4>
                <p>Get immediate access to Live Trading & the Manual</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number" style={{ background: plan.gradient }}>3</div>
              <div className="step-content">
                <h4>Schedule Sessions</h4>
                <p>Coach CJ will reach out to schedule your first session</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* FAQ or Terms */}
        <div className="terms-section">
          <p>
            <strong>Note:</strong> After your {plan.liveTradingMonths}-month free period, 
            your Live Trading membership will automatically continue at the regular monthly rate.
            You can cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MentorshipPage;
